import { useMemo } from 'react';
import { type LinearGradientProps } from 'expo-linear-gradient';
import { ApplicationType } from '@unifyapps/defs/types/interface';
import { useInterfaceStore } from '../no-code/stores/InterfaceStore';
import { safeJsonParse } from './json';

type UseCustomStylesProps = {
  customCSS: string | undefined;
  style?: object | undefined;
};

type UseCustomStylesReturn = {
  gradientConfig: LinearGradientProps | undefined;
  neumorphicStyle: object | undefined;
  finalStyle: object | undefined;
};

export default function useCustomStyles({
  customCSS,
  style,
}: UseCustomStylesProps): UseCustomStylesReturn {
  const applicationType = useInterfaceStore().use.record.applicationType();
  const isMobile = applicationType === ApplicationType.MOBILE;

  return useMemo(() => {
    if (!customCSS || !isMobile) {
      return {
        gradientConfig: undefined,
        neumorphicStyle: undefined,
        finalStyle: style,
      };
    }

    const parsedStyles = safeJsonParse(customCSS, {}) as {
      linearGradient?: LinearGradientProps;
      neumorphicStyle?: object | undefined;
      [key: string]: LinearGradientProps | object | undefined | string | number | boolean;
    };

    const linearGradient =
      parsedStyles.linearGradient && typeof parsedStyles.linearGradient === 'object'
        ? parsedStyles.linearGradient
        : undefined;

    const neumorphicStyle =
      parsedStyles.neumorphicStyle && typeof parsedStyles.neumorphicStyle === 'object'
        ? parsedStyles.neumorphicStyle
        : undefined;

    const { linearGradient: _, neumorphicStyle: __, ...restStyles } = parsedStyles;

    const finalStyle = { ...style, ...restStyles };
    return {
      gradientConfig: linearGradient,
      neumorphicStyle,
      finalStyle,
    };
  }, [customCSS, style, isMobile]);
}
